import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { API_CONFIG, ApiConfig } from './api-config';
import { ApiResponse } from '@shared-models';
import type {
  BankAccount,
  BankAccountExtraction,
  BankAccountFunding,
  BankAccountPageResponse,
  BankAccountHistoryAction,
  BankAccountDocument,
  BankAccountExtractionLink,
  BankAccountMovement,
  BankAccountTab,
} from '@shared-models';

interface PageableData {
  content: any[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

@Injectable({ providedIn: 'root' })
export class BankAccountService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/treasury/account-banks`;
  }

  /**
   * Normaliza el status de mayúsculas a minúsculas
   */
  private normalizeStatus(status: string | undefined): 'active' | 'inactive' {
    if (!status) return 'active';
    return status.toLowerCase() as 'active' | 'inactive';
  }

  /**
   * Normaliza el status de movimiento
   */
  private normalizeMovementStatus(
    status: string | undefined,
  ): 'pendiente' | 'ingresado' | 'cancelado' {
    if (!status) return 'pendiente';
    const normalized = status.toLowerCase();
    if (normalized === 'ingresado') return 'ingresado';
    if (normalized === 'cancelado') return 'cancelado';
    return 'pendiente';
  }

  /**
   * Transforma la respuesta del backend al modelo del frontend para cuentas bancarias
   */
  private mapToBankAccount(item: any): BankAccount {
    return {
      id: item.id ?? 0,
      banco: item.banco || item.bankName || '',
      cuenta: item.cuenta || item.accountNumber || '',
      tipo: item.tipo || item.type || '',
      divisa: item.divisa || item.currency || '',
      grupo: item.grupo || item.group || '',
      empresa: item.empresa || item.company || '',
      pais: item.pais || item.country || '',
      ingresos: item.ingresos || item.income || '0',
      egresos: item.egresos || item.expense || '0',
      saldo: item.saldo || item.balance || '0',
      status: this.normalizeStatus(item.status),
    };
  }

  /**
   * Transforma la respuesta del backend al modelo del frontend para extracciones
   */
  private mapToExtraction(item: any): BankAccountExtraction {
    return {
      id: item.id ?? 0,
      fecha: item.fecha || item.date || '',
      empresa: item.empresa || item.company || '',
      cuenta: item.cuenta || item.account || '',
      folio: item.folio || item.reference || '',
      claveRastreo: item.claveRastreo || item.trackingKey || '',
      usuario: item.usuario || item.user || '',
      importe: item.importe || item.amount || '',
      status: this.normalizeMovementStatus(item.status),
    };
  }

  /**
   * Transforma la respuesta del backend al modelo del frontend para fondeos
   */
  private mapToFunding(item: any): BankAccountFunding {
    return this.mapToExtraction(item);
  }

  /**
   * Obtiene las cuentas bancarias según el tipo de tab
   * @param type 'gestion' | 'extracciones' | 'fondeos'
   */
  getAll(
    type: BankAccountTab = 'gestion',
    page = 0,
    size = 20,
    sort = 'createdAt',
    direction: 'asc' | 'desc' = 'desc',
    search?: string,
    status?: string,
  ): Observable<BankAccountPageResponse> {
    let params = new HttpParams()
      .set('type', type)
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sort', sort)
      .set('direction', direction);

    if (search && search.trim()) {
      params = params.set('search', search.trim());
    }
    if (status && status.trim()) {
      params = params.set('status', status.trim());
    }

    return this.httpClient
      .get<ApiResponse<PageableData>>(this.apiUrl, { params })
      .pipe(
        map((response) => {
          const pageableData = response.data;
          const items = pageableData?.content || [];

          let mappedItems:
            | BankAccount[]
            | BankAccountExtraction[]
            | BankAccountFunding[];

          if (type === 'gestion') {
            mappedItems = items.map((item: any) => this.mapToBankAccount(item));
          } else if (type === 'extracciones') {
            mappedItems = items.map((item: any) => this.mapToExtraction(item));
          } else {
            mappedItems = items.map((item: any) => this.mapToFunding(item));
          }

          return {
            items: mappedItems,
            page: pageableData?.page ?? 0,
            size: pageableData?.size ?? size,
            totalElements: pageableData?.totalElements ?? 0,
            totalPages: pageableData?.totalPages ?? 0,
          };
        }),
      );
  }

  /**
   * Obtiene una cuenta bancaria por ID
   */
  getById(id: string): Observable<BankAccount> {
    return this.httpClient
      .get<ApiResponse<any>>(`${this.apiUrl}/${id}`)
      .pipe(map((response) => this.mapToBankAccount(response.data)));
  }

  /**
   * Crea una nueva cuenta bancaria
   */
  create(data: Partial<BankAccount>): Observable<{ message: string }> {
    return this.httpClient
      .post<{ success: boolean; message: string }>(this.apiUrl, data)
      .pipe(
        map((response) => ({
          message: response.message || 'Cuenta bancaria creada exitosamente',
        })),
      );
  }

  /**
   * Actualiza una cuenta bancaria existente
   */
  update(
    id: string,
    data: Partial<BankAccount>,
  ): Observable<{ message: string }> {
    return this.httpClient
      .put<{ success: boolean; message: string }>(`${this.apiUrl}/${id}`, data)
      .pipe(
        map((response) => ({
          message:
            response.message || 'Cuenta bancaria actualizada exitosamente',
        })),
      );
  }

  /**
   * Elimina una cuenta bancaria
   */
  delete(id: string): Observable<{ message: string }> {
    return this.httpClient
      .delete<{ success: boolean; message: string }>(`${this.apiUrl}/${id}`)
      .pipe(
        map((response) => ({
          message: response.message || 'Cuenta bancaria eliminada exitosamente',
        })),
      );
  }

  /**
   * Actualiza el status de una cuenta bancaria
   */
  updateStatus(
    id: string,
    status: 'active' | 'inactive',
  ): Observable<{ message: string }> {
    return this.httpClient
      .patch<{
        success: boolean;
        message: string;
      }>(`${this.apiUrl}/${id}/status`, { status })
      .pipe(
        map((response) => ({
          message: response.message || 'Status actualizado exitosamente',
        })),
      );
  }

  /**
   * Obtiene el historial de acciones de una cuenta bancaria
   */
  getHistory(
    id: string,
    page = 0,
    size = 20,
  ): Observable<BankAccountHistoryAction[]> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    return this.httpClient
      .get<ApiResponse<PageableData>>(`${this.apiUrl}/${id}/history`, {
        params,
      })
      .pipe(
        map((response) => {
          const items = response.data?.content || [];
          return items.map((action: any) => ({
            id: action.id,
            fechaHora: action.date || action.fechaHora || '',
            accion: action.action || action.accion || 'Acción',
            usuario: action.user || action.usuario || 'Usuario',
            ip: action.ip || '',
            dispositivo: action.device || action.dispositivo || '',
            navegador: action.browser || action.navegador || '',
            ubicacion: action.location || action.ubicacion || '',
          }));
        }),
      );
  }

  /**
   * Obtiene los documentos de una cuenta bancaria
   */
  getDocuments(
    id: string,
    page = 0,
    size = 20,
  ): Observable<BankAccountDocument[]> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    return this.httpClient
      .get<ApiResponse<PageableData>>(`${this.apiUrl}/${id}/documents`, {
        params,
      })
      .pipe(
        map((response) => {
          const items = response.data?.content || [];
          return items.map((doc: any) => ({
            id: doc.id,
            nombre: doc.name || doc.nombre || '',
            tipo: doc.type || doc.tipo || '',
            fecha: doc.date || doc.fecha || '',
            status: this.normalizeStatus(doc.status),
          }));
        }),
      );
  }

  /**
   * Obtiene los enlaces de extracciones de una cuenta bancaria
   */
  getExtractionLinks(
    id: string,
    page = 0,
    size = 20,
  ): Observable<BankAccountExtractionLink[]> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    return this.httpClient
      .get<ApiResponse<PageableData>>(`${this.apiUrl}/${id}/extraction-links`, {
        params,
      })
      .pipe(
        map((response) => {
          const items = response.data?.content || [];
          return items.map((link: any) => ({
            id: link.id,
            nombre: link.name || link.nombre || '',
            status: this.normalizeStatus(link.status),
          }));
        }),
      );
  }

  /**
   * Obtiene los movimientos de bóveda de una cuenta bancaria
   */
  getMovements(
    id: string,
    page = 0,
    size = 20,
    type?: string,
  ): Observable<BankAccountMovement[]> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    if (type) {
      params = params.set('type', type);
    }

    return this.httpClient
      .get<ApiResponse<PageableData>>(`${this.apiUrl}/${id}/movements`, {
        params,
      })
      .pipe(
        map((response) => {
          const items = response.data?.content || [];
          return items.map((mov: any) => ({
            id: mov.id,
            fechaHora: mov.dateTime || mov.fechaHora || '',
            tipo: mov.movementType || mov.tipo || 'Extracción',
            cantidad: mov.amount || mov.cantidad || '0',
            enlace: mov.link || mov.enlace || '',
            dispositivo: mov.device || mov.dispositivo || '',
            usuario: mov.user || mov.usuario || '',
            ubicacion: mov.location || mov.ubicacion || '',
            ipPublica: mov.publicIp || mov.ipPublica || '',
            ipPrivada: mov.privateIp || mov.ipPrivada || '',
          }));
        }),
      );
  }

  /**
   * Procesa una extracción (cambia su status)
   */
  processExtraction(
    id: string,
    action: 'ingresar' | 'cancelar',
  ): Observable<{ message: string }> {
    return this.httpClient
      .post<{
        success: boolean;
        message: string;
      }>(`${this.apiUrl}/extractions/${id}/${action}`, {})
      .pipe(
        map((response) => ({
          message: response.message || `Extracción ${action} exitosamente`,
        })),
      );
  }

  /**
   * Procesa un fondeo (cambia su status)
   */
  processFunding(
    id: string,
    action: 'ingresar' | 'cancelar',
  ): Observable<{ message: string }> {
    return this.httpClient
      .post<{
        success: boolean;
        message: string;
      }>(`${this.apiUrl}/fundings/${id}/${action}`, {})
      .pipe(
        map((response) => ({
          message: response.message || `Fondeo ${action} exitosamente`,
        })),
      );
  }

  /**
   * Exporta la lista de cuentas bancarias a PDF
   */
  exportPdf(options?: {
    title?: string;
    type?: BankAccountTab;
    status?: string;
    search?: string;
    page?: number;
    size?: number;
  }): Observable<Blob> {
    let params = new HttpParams()
      .set('title', options?.title || 'Reporte de cuentas bancarias')
      .set('type', options?.type || 'gestion')
      .set('status', options?.status || '')
      .set('search', options?.search || '')
      .set('page', (options?.page ?? 0).toString())
      .set('size', (options?.size ?? 1000).toString());

    return this.httpClient.get(
      `${this.apiConfig.apiUrl}/export/pdf/account-banks`,
      {
        params,
        responseType: 'blob',
      },
    );
  }

  /**
   * Exporta la lista de cuentas bancarias a Excel
   */
  exportExcel(options?: {
    title?: string;
    type?: BankAccountTab;
    status?: string;
    search?: string;
    page?: number;
    size?: number;
  }): Observable<Blob> {
    let params = new HttpParams()
      .set('title', options?.title || 'Reporte de cuentas bancarias')
      .set('type', options?.type || 'gestion')
      .set('status', options?.status || '')
      .set('search', options?.search || '')
      .set('page', (options?.page ?? 0).toString())
      .set('size', (options?.size ?? 1000).toString());

    return this.httpClient.get(
      `${this.apiConfig.apiUrl}/export/excel/account-banks`,
      {
        params,
        responseType: 'blob',
      },
    );
  }

  /**
   * Exporta el historial de una cuenta bancaria a PDF
   */
  exportHistoryPdf(
    id: string,
    options?: {
      title?: string;
    },
  ): Observable<Blob> {
    const params = new HttpParams().set(
      'title',
      options?.title || 'Historial cuenta bancaria',
    );

    return this.httpClient.get(
      `${this.apiConfig.apiUrl}/export/pdf/history/account-banks/${id}`,
      {
        params,
        responseType: 'blob',
      },
    );
  }

  /**
   * Exporta el historial de una cuenta bancaria a Excel
   */
  exportHistoryExcel(
    id: string,
    options?: {
      title?: string;
    },
  ): Observable<Blob> {
    const params = new HttpParams().set(
      'title',
      options?.title || 'Historial cuenta bancaria',
    );

    return this.httpClient.get(
      `${this.apiConfig.apiUrl}/export/excel/history/account-banks/${id}`,
      {
        params,
        responseType: 'blob',
      },
    );
  }
}
